import { ref, reactive, computed, onBeforeUnmount, toRefs, openBlock, createElementBlock, normalizeClass, createElementVNode, normalizeStyle, renderSlot, Fragment, renderList } from 'vue';
import propTypes from './utils/prop-types';
import { CLICK_EVENT, CLOSE_EVENT, OPEN_EVENT } from './utils/constants';
import { createComponent } from './utils/create-component';
import useTouch from './utils/hooks/use-touch';
import { preventDefault } from './utils/dom';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var props = {
  /**
   * 左侧按钮组
   */
  left: propTypes.array().def([]),
  /**
   * 右侧按钮组
   */
  right: propTypes.array().def([]),
  /**
   * 该参数的值会作为handler的第一个参数带回，适合列表的使用场景
   */
  handlerContext: propTypes.oneOfType([propTypes.number(), propTypes.string(), propTypes["boolean"](), propTypes["function"](), propTypes.symbol(), propTypes.array(), propTypes.date(), propTypes.object()])
};
var emits = [CLICK_EVENT, CLOSE_EVENT, OPEN_EVENT];

var _createComponent = createComponent('swipe-cell'),
  create = _createComponent.create,
  className = _createComponent.className;
var SHOW_BUTTON_OFFSET = 40;
var script = create({
  props: props,
  emits: emits,
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
      emit = _ref.emit;
    // 拖动开始时的偏移位置
    var baseOffsetX = 0;
    // 左侧按钮区域宽度
    var leftWidth = 0;
    // 右侧按钮区域宽度
    var rightWidth = 0;
    // 拖动开始前记录上次位置
    var lastOffset = 0;
    var swipeCell = ref();
    var innerRef = ref();
    var leftRef = ref();
    var rightRef = ref();
    var touch = useTouch();
    var state = reactive({
      // 偏移量
      offset: lastOffset,
      // 是否执行css动画
      animation: false,
      // 是否滑动中
      swiping: false
    });
    // 拖动样式
    var innerStyle = computed(function () {
      return {
        transform: "translateX(".concat(state.offset, "px)"),
        transition: state.animation ? "all 300ms" : ''
      };
    });
    /**
     * @description: 关闭滑动单元格
     * @param {*} position 位置
     * @return {*}
     */
    function close(position) {
      emit(CLOSE_EVENT, position);
    }
    /**
     * @description: 打开滑动单元格
     * @param {*} position 位置
     * @return {*}
     */
    function open(position) {
      emit(OPEN_EVENT, position);
    }
    // 限制拖动距离
    function limitOffsetX(val) {
      return Math.min(Math.max(-rightWidth, val), leftWidth);
    }
    function onTouchStart(e) {
      lastOffset = state.offset;
      state.animation = false;
      /* istanbul ignore next */
      if (!swipeCell.value || !innerRef.value || !leftRef.value || !rightRef.value) return;
      // 拖动开始前获取左右按钮区域大小
      leftWidth = leftRef.value.offsetWidth;
      /* istanbul ignore next */
      rightWidth = rightRef.value.offsetWidth;
      // 获取当前偏移量
      baseOffsetX = innerRef.value.getBoundingClientRect().x - swipeCell.value.getBoundingClientRect().x;
      state.offset = baseOffsetX;
      touch.start(e);
    }
    function onTouchMove(e) {
      touch.move(e);
      if (touch.state.direction !== 'horizontal') return;
      // 触发拖动时阻止默认事件
      preventDefault(e, true);
      // 拖动位置变化
      state.offset = limitOffsetX(baseOffsetX + touch.state.deltaX);
    }
    function onTouchEnd(e) {
      state.animation = true;
      // 如果偏移是0，直接返回
      if (state.offset === 0) return;
      // 设置状态为swiping(offset不为0的情况都会执行动画)
      state.swiping = true;
      // 如果没有direction认为是点击操作，重置offset
      if (!touch.state.direction) {
        // 如果点击的是左右两侧按钮区域，则延迟触发归位，否则在IOS手机按钮点击事件触发不了（300m延迟）
        var swipeInner = innerRef.value;
        var swipeItemEl = e.target;
        if (swipeInner !== swipeItemEl.offsetParent) {
          setTimeout(function () {
            state.offset = 0;
          }, 300);
        } else {
          state.offset = 0;
        }
        close('cell');
        return;
      }
      if (touch.state.direction !== 'horizontal') return;
      var deltaX = touch.state.deltaX;
      // 判断滑动方向，根据offset判断下一个位置
      if (touch.state.offsetX < SHOW_BUTTON_OFFSET && touch.speed.x < 0.08) {
        state.offset = lastOffset;
        return;
      }
      if (deltaX > 0) {
        // 右滑
        if (state.offset < 0) {
          state.offset = 0;
          close('right');
        } else {
          open('right');
          state.offset = leftWidth;
        }
      } else if (deltaX < 0) {
        // 左滑
        if (state.offset > 0) {
          state.offset = 0;
          close('left');
        } else {
          open('left');
          state.offset = -rightWidth;
        }
      }
    }
    function onTransitionEnd() {
      state.swiping = false;
    }
    function onButtonClick(btnConfig) {
      if (typeof btnConfig.handler === 'function') {
        btnConfig.handler(props.handlerContext);
      }
    }
    function onContentClick() {
      if (state.offset === 0 && !state.swiping) {
        emit(CLICK_EVENT);
      }
    }
    // 点击外部区域时重置位置
    function onOuterTouchStart(e) {
      /* istanbul ignore next */
      if (!swipeCell.value) return;
      if (!swipeCell.value.contains(e.target)) {
        state.offset = 0;
        close('outside');
      }
    }
    document.addEventListener('touchstart', onOuterTouchStart);
    onBeforeUnmount(function () {
      document.removeEventListener('touchstart', onOuterTouchStart);
    });
    return _objectSpread2(_objectSpread2({}, toRefs(state)), {}, {
      swipeCell: swipeCell,
      innerRef: innerRef,
      leftRef: leftRef,
      rightRef: rightRef,
      onTouchStart: onTouchStart,
      onTouchMove: onTouchMove,
      onTouchEnd: onTouchEnd,
      onButtonClick: onButtonClick,
      onContentClick: onContentClick,
      onTransitionEnd: onTransitionEnd,
      className: className,
      innerStyle: innerStyle,
      slots: slots
    });
  }
});

var _hoisted_1 = ["innerHTML", "onClick"];
var _hoisted_2 = ["innerHTML", "onClick"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    "class": normalizeClass(_ctx.className),
    onTouchstart: _cache[2] || (_cache[2] = function () {
      return _ctx.onTouchStart && _ctx.onTouchStart.apply(_ctx, arguments);
    }),
    onTouchmove: _cache[3] || (_cache[3] = function () {
      return _ctx.onTouchMove && _ctx.onTouchMove.apply(_ctx, arguments);
    }),
    onTouchend: _cache[4] || (_cache[4] = function () {
      return _ctx.onTouchEnd && _ctx.onTouchEnd.apply(_ctx, arguments);
    }),
    onTouchcancel: _cache[5] || (_cache[5] = function () {
      return _ctx.onTouchEnd && _ctx.onTouchEnd.apply(_ctx, arguments);
    }),
    ref: "swipeCell"
  }, [createElementVNode("div", {
    ref: "innerRef",
    "class": normalizeClass("".concat(_ctx.className, "__inner")),
    style: normalizeStyle(_ctx.innerStyle),
    onTransitionend: _cache[1] || (_cache[1] = function () {
      return _ctx.onTransitionEnd && _ctx.onTransitionEnd.apply(_ctx, arguments);
    })
  }, [createElementVNode("div", {
    ref: "leftRef",
    "class": normalizeClass("".concat(_ctx.className, "__left"))
  }, [renderSlot(_ctx.$slots, "left", {}, function () {
    return [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.left, function (item, index) {
      return openBlock(), createElementBlock("div", {
        key: index,
        "class": normalizeClass("".concat(_ctx.className, "__btn")),
        style: normalizeStyle(item.style),
        innerHTML: item.content,
        onClick: function onClick($event) {
          return _ctx.onButtonClick(item);
        }
      }, null, 14 /* CLASS, STYLE, PROPS */, _hoisted_1);
    }), 128 /* KEYED_FRAGMENT */))];
  })], 2 /* CLASS */), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__content")),
    onClick: _cache[0] || (_cache[0] = function () {
      return _ctx.onContentClick && _ctx.onContentClick.apply(_ctx, arguments);
    })
  }, [renderSlot(_ctx.$slots, "default", {
    swiping: _ctx.swiping
  })], 2 /* CLASS */), createElementVNode("div", {
    ref: "rightRef",
    "class": normalizeClass("".concat(_ctx.className, "__right"))
  }, [renderSlot(_ctx.$slots, "right", {}, function () {
    return [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.right, function (item, index) {
      return openBlock(), createElementBlock("div", {
        key: index,
        "class": normalizeClass("".concat(_ctx.className, "__btn")),
        style: normalizeStyle(item.style),
        innerHTML: item.content,
        onClick: function onClick($event) {
          return _ctx.onButtonClick(item);
        }
      }, null, 14 /* CLASS, STYLE, PROPS */, _hoisted_2);
    }), 128 /* KEYED_FRAGMENT */))];
  })], 2 /* CLASS */)], 38 /* CLASS, STYLE, HYDRATE_EVENTS */)], 34 /* CLASS, HYDRATE_EVENTS */);
}

script.render = render;
script.__file = "components/src/component/swipe-cell/swipe-cell.vue";

var _SwipeCell = script;

export { _SwipeCell as default };
